/** Customer-facing label for shared / managed account SKUs (replaces legacy “lifetime” wording). */
export const SHARED_ACCESS_LABEL = 'Shared Access'

/** @deprecated Use SHARED_ACCESS_LABEL */
export const SHARED_ACCOUNT_ACCESS_LABEL = SHARED_ACCESS_LABEL

export function isSharedAccessListing(name: string): boolean {
  return /\bshared access\b/i.test(name)
}

/** @deprecated Use isSharedAccessListing */
export const isSharedAccountAccessListing = isSharedAccessListing

/** Products with bespoke PDP copy — do not apply the generic shared-access template. */
export function usesCustomSharedAccessCopy(name: string): boolean {
  if (name === 'Netflix Key Access (Shared Access)') return true
  if (name === 'Youtube Premium Shared Access FA') return true
  if (name === 'Prime Video 6 Months FA') return true
  if (/\bkey access\b/i.test(name)) return true
  return false
}

export function sharedAccessDisplayLabel(productName: string): string {
  const n = productName.trim()
  if (/chatgpt/i.test(n)) return 'Shared ChatGPT+ Account'
  const stripped = n.replace(/\s+shared access(\s+fa)?$/i, '').trim()
  if (stripped && stripped !== n) return `Shared ${stripped} Account`
  return n
}

export type SharedAccessProductCopy = {
  deliveryInfo: string
  description: string
  shortDescription: string
  features: string[]
  faqs: Array<{ q: string; a: string }>
}

/** Standard shared-access terms for PDP, cards, and checkout transparency. */
export function buildSharedAccessProductCopy(productName: string, priceUsd: number): SharedAccessProductCopy {
  const label = sharedAccessDisplayLabel(productName)
  return {
    deliveryInfo:
      'Delivered after payment clears (usually within minutes). Shared login — access duration not guaranteed. No refunds after successful delivery.',
    shortDescription: `${label} — shared credentials, $${priceUsd.toFixed(2)}.`,
    description: [
      `${label}: you receive credentials for a shared / managed login, not a private solo subscription with a fixed term from us.`,
      'Delivery is sent after your payment clears — typically within minutes via support or automated fulfillment.',
      'How long access lasts depends on the provider and account health; we do not guarantee a specific duration or “lifetime” entitlement.',
      'No replacements or cash refunds after successful delivery, except where our policy covers invalid credentials on arrival.',
      'If login details do not work on first use, contact support within the verification window with your order reference and proof.',
      'Do not change passwords, recovery email, billing, or plan settings unless the listing explicitly allows it — that can end access immediately.',
      'By purchasing, you acknowledge the risks associated with shared account services and accept that provider action outside our control can end access without refund.',
    ].join(' '),
    features: [
      'Shared access account',
      'Delivered after payment clears (typically within minutes)',
      'Access duration is not guaranteed',
      'No replacements or refunds after successful delivery',
      'Invalid credentials on arrival: contact support within the verification window',
      'By purchasing, you acknowledge the risks of shared account services',
    ],
    faqs: [
      {
        q: 'What am I buying?',
        a: `${label} means shared / managed login credentials. It is not a forever guarantee or a private account you fully own unless the listing says otherwise.`,
      },
      {
        q: 'When do I receive access?',
        a: 'After your payment is confirmed. Delivery is usually within minutes; follow any instructions sent with your order.',
      },
      {
        q: 'Can I get a refund or replacement later?',
        a: 'Not after successful delivery under our standard shared-access policy. If credentials are invalid on arrival, contact support promptly inside the verification window with proof.',
      },
      {
        q: 'Can I change the password or email?',
        a: 'Only if the listing allows it. Unauthorized credential or billing changes can trigger provider security checks and end access without refund.',
      },
    ],
  }
}
